import { useState, useRef, useEffect } from "react";
import { useUser } from "../../../contexts/UserContext";
import { User } from "../../../models/User";
import { Checkbox } from "../../../design/checkbox";
import { Field, Label } from "../../../design/fieldset";
import { useAuth } from "../../../contexts/AuthContext";
import { useTeamMember } from "../../../contexts/TeamMemberContext";
import { useMatter } from "../../../contexts/MatterContext";

interface ToFieldProps {
  value: User["_id"][];
  onChange: (selectedContacts: User["_id"][]) => void;
}

const ToField: React.FC<ToFieldProps> = ({ value, onChange }) => {
  const { user } = useAuth();
  const { currentMatter } = useMatter();
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const users = currentMatter?.users || [];

  const toggleDropdown = () => setShowDropdown((prev) => !prev);

  const toggleContact = (id: string) => {
    const isSelected = value.includes(id);
    const newSelection = isSelected
      ? value.filter((contactId) => contactId !== id)
      : [...value, id];
    onChange(newSelection);
    setShowDropdown(false);
  };

  const selectedNames = users
    .filter((c) => c._id !== user?.uid)
    .filter((c) => value.includes(c._id))
    .map((c) => c.name)
    .join(", ");

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  return (
    <Field ref={containerRef}>
      <Label>To:</Label>
      <div
        className="p-2 bg-white border rounded cursor-pointer"
        onClick={toggleDropdown}
      >
        {selectedNames || "Select recipients..."}
      </div>

      {showDropdown && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow">
          {users
            .filter((c) => c._id !== user?.uid)
            .map((contact) => (
              <div
                key={contact._id}
                className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-100"
                onClick={() => toggleContact(contact._id)}
              >
                <div className="flex flex-col">
                  <span>{contact.name}</span>
                  <span className="text-sm text-gray-500">{contact.email}</span>
                </div>
                <Checkbox checked={value.includes(contact._id)} />
              </div>
            ))}
        </div>
      )}
    </Field>
  );
};

export default ToField;
